import axios from 'axios'
import mitt from 'mitt'
import * as R from 'ramda'
import React from 'react'
import {useLocation, useNavigate} from 'react-router-dom'
import type {SWRConfiguration} from 'swr'
import useSWR from 'swr'
import {v4 as uuid} from 'uuid'

import {LoadingPage} from '@pleo-io/telescope'
import {generateKeyPair} from '@pleo-io/web-crypto'

import {request} from '@product-web/api/api'
import * as apiAuthPin from '@product-web/api/auth/pin'
import {OtpType} from '@product-web/api/auth/register'
import {RequestAuth, RequestScope} from '@product-web/api/types'
import {invariant} from '@product-web/error/invariant'
import {reportError} from '@product-web/error/report'
import {Freshchat} from '@product-web/external/freshchat'
import {usePrevious} from '@product-web/react-utils'
import {notEmpty} from '@product-web/utils'
import cookie from '@product-web/web-platform/cookie'
import {localStorage} from '@product-web/web-platform/storage/local'
import {sessionStorage} from '@product-web/web-platform/storage/session'
import {removeUserParams as removeParamsFromRegistration} from '@product-web-features/register/use-register-state'

import {PIN_CANCELLED_BY_USER_ERROR_STATUS} from '~/auth/constants'
import {getJwtExpiryMin, getJwtExpiryMs, isJwt} from '~/auth/jwt'
import ModalPinLogin from '~/auth/pin-modal/pin-modal'
import {getTokenPayload, useTokenData} from '~/auth/token-data'
import config from 'config'

import {getOAuthCallbackParams} from './oauth'
import {useUserMutations} from './user'

export enum SessionStatus {
    Unknown = 'unknown',
    LoggedIn = 'logged-in',
    LoggedOut = 'logged-out',
}

// Indicates the type of session the user is on.
// - Otp is the 2FA way of session handling that requires a One-Time-Password (OTP) sent via SMS, a valid email, and a valid passcode.
//   Subsequent logins after an initial login on a trusted device will only require the passcode.
// - Legacy is login that requires email an password to authenticate.
enum SessionType {
    Otp = 'otp',
    Legacy = 'legacy',
}

// This key is for session storage.
// It is used to store the session type of a user when logging in since a user can have either "Otp" or "Legacy" session.
const SESSION_TYPE_KEY = 'sessionType'
// This key is to identify the primary account for a user with multiple logged in sessions
export const PRIMARY_ACCOUNT_EMAIL = 'primaryAccountEmail'

// Cookie name of a cookie used to store the session status of a user when logging in or out.
// This is a read-only value that is used by the commercial website to
const SESSION_STATUS_KEY = 'isLoggedIn'

type SessionData = {
    accessToken?: string | null
    accessTokens?: string[] | null // Active tokens on a trusted session or device
    trustedEmails?: string[] | null // Lets us know the emails on a trusted session or device
    email?: string | null // Email associated with active session or account
    companyId?: string | null // Identifier for current active company a user is viewing. This enables users like bookkeepers to access accounts they manage
    partnerId?: string | null // Identifier for user with partner role at login
    location?: string // Identifier for location when switch account is invoked
}

type LogoutOptions = {afterLogoutPath?: string; onLogout?: () => void}
export type SessionContextAPI = {
    accessToken: null | string
    accountAccessTokens: string[] | null
    email: null | string
    status: SessionStatus
    trustedEmails: string[] | null
    logout: ReturnType<typeof getMutations>['logout']
    login: ReturnType<typeof getMutations>['login']
    verifiedLogin: ReturnType<typeof getMutations>['verifiedLogin']
    register: ReturnType<typeof getMutations>['register']
    registerAdmin: ReturnType<typeof getMutations>['registerAdmin']
    loginWithPasscode: ReturnType<typeof getMutations>['loginWithPasscode']
    loginWithToken: ReturnType<typeof getMutations>['loginWithToken']
}

export const LEGACY_SESSION_ROUTE = `${config.endpoints.auth}/session/refresh`
export const SESSION_ROUTE = `${config.endpoints.auth}/sca/session`
export const SESSION_REFRESH_ROUTE = `${config.endpoints.auth}/sca/refresh`
export const SESSION_LOGIN_ROUTE = `${config.endpoints.auth}/sca/login`
export const SESSION_VERIFIED_LOGIN_ROUTE = `${config.endpoints.auth}/sca/verified-login`
export const SESSION_SET_TRUSTED_ROUTE = `${config.endpoints.auth}/sca/session/set-trusted`
export const VERIFY_LOGIN_ROUTE = `${config.endpoints.auth}/sca/verify-login-attempt`
export const SESSION_LOGOUT_ROUTE = `${config.endpoints.auth}/sca/logout`
export const LEGACY_SESSION_LOGOUT_ROUTE = `${config.endpoints.auth}/session/logout`

async function logoutFreshchat() {
    const freshworks = window.fcWidget
    if (freshworks) {
        try {
            await Freshchat.clearUser()
        } catch (error) {
            reportError(error, 'Failed to logout Freshchat')
        }
    }
}

////////////////////////////////////////////////////////////////////////////////
// #region Session Context

export const SessionContext = React.createContext<SessionContextAPI>({
    accessToken: null,
    accountAccessTokens: null,
    email: null,
    status: SessionStatus.Unknown,
    trustedEmails: null,
    logout: async () => Promise.resolve(),
    login: async () => Promise.resolve(),
    verifiedLogin: async () =>
        Promise.resolve({result: 'email_challenge_required', authenticationData: null}),
    register: async () => Promise.resolve(),
    registerAdmin: async () => Promise.resolve(),
    loginWithPasscode: async () => Promise.resolve(),
    loginWithToken: async () => Promise.resolve({}),
})

// This provider wraps the entire app. It's responsible for fetching session info from Kerberos,
// setting this data to sessionStore, registering event handler for sessionStore changes
// and exposing sessionData to all pages via context. It's also re-fetching the session info when the tab
// gains focus, to e.g. account for long lived tabs where the session expires, or user's logging
// out in a different tab.
export const SessionProvider: React.FC = ({children}) => {
    useForceSessionCleanup()

    const [isLoggingOut, setIsLoggingOut] = React.useState(false)
    const [sessionData, setSessionData] = React.useState<SessionData | undefined>(
        sessionStore.get(),
    )

    // SWR is used for data fetching & revalidation, however fetcher sets received data in sessionStore,
    // so that request methods have access to sessionData outside React.Context,
    // this is why we do not return data from the hook, but handling events from sessionStore event emitter
    useSWR<SessionData | undefined, Error>(SESSION_ROUTE, fetchSession, {
        onErrorRetry,
        revalidateOnFocus: true,
    })

    // Listener for session update to set latest value from sessionStorage to local state
    // (see getSessionStore description below)
    useSessionUpdatedEventListener(setSessionData)

    const accessToken = sessionData?.accessToken
    const accessTokens = sessionData?.accessTokens
    const trustedEmails = sessionData?.trustedEmails
    const email = sessionData?.email

    const sessionStatus = deriveSessionStatusFromSessionData(sessionData ?? {}, isLoggingOut)

    // Whenever we go from logged in state to logged out state,
    // we want to perform a full clean-up of session data.
    // This will usually happen when the user's session expires,
    // or they log out in another tab.
    const previousSessionStatus = usePrevious(sessionStatus)

    React.useEffect(() => {
        if (
            sessionStatus === SessionStatus.LoggedOut &&
            previousSessionStatus === SessionStatus.LoggedIn
        ) {
            cleanupSession()
        }
    }, [sessionStatus])

    // Any time the session status changes, we create/update a cookie that
    // indicates whether the user is logged in or not. This is read e.g. in the
    // website to show an alternative UI for logged-in users.
    React.useEffect(() => {
        if (sessionStatus === SessionStatus.LoggedIn) {
            cookie.set(SESSION_STATUS_KEY, true, 1000 * 60 * 60 * 6)
        } else {
            cookie.remove(SESSION_STATUS_KEY)
        }
    }, [sessionStatus])

    const mutations = getMutations(() => setIsLoggingOut(true))

    const sessionContextValue: SessionContextAPI = {
        status: sessionStatus,
        email: email ?? null,
        accessToken: accessToken ?? null,
        accountAccessTokens: accessTokens ?? null,
        trustedEmails: trustedEmails ?? null,
        logout: mutations.logout,
        login: mutations.login,
        verifiedLogin: mutations.verifiedLogin,
        register: mutations.register,
        registerAdmin: mutations.registerAdmin,
        loginWithPasscode: mutations.loginWithPasscode,
        loginWithToken: mutations.loginWithToken,
    }

    if (sessionStatus === SessionStatus.Unknown && !trustedEmails?.length) {
        // We do not yet know whether we can refresh session or no
        return <LoadingPage data-testid="loading-indicator" />
    }

    return <SessionContext.Provider value={sessionContextValue}>{children}</SessionContext.Provider>
}

// #endregion Session Context
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Private helper methods

function getAccessTokenFromURL() {
    const oauthParams = getOAuthCallbackParams(window.location.hash)

    if (!oauthParams) {
        return undefined
    }

    if (oauthParams.error) {
        return {accessToken: null}
    }

    return {accessToken: oauthParams.accessToken || null}
}

// Creates Event emitter that helps to track session inside & outside of react context
// main reason for such implementation is `request` method used for PUT/POST/etc. requests
// executed outside of context availability - expected to be changed after redux refactors
const getSessionStore = () => {
    type Events = {
        sessionUpdated: SessionData
    }
    let sessionData: SessionData | undefined = getAccessTokenFromURL()
    const emitter = mitt<Events>()
    const get = () => {
        return sessionData
    }

    const set = (nextSessionData: SessionData) => {
        const hasAccessTokensChanged = !isSame(
            sessionData?.accessTokens ?? [],
            nextSessionData?.accessTokens ?? [],
        )
        const hasTrustedEmailsChanged = !isSame(
            sessionData?.trustedEmails ?? [],
            nextSessionData?.trustedEmails ?? [],
        )

        if (
            hasAccessTokensChanged ||
            hasTrustedEmailsChanged ||
            sessionData?.accessToken !== nextSessionData.accessToken ||
            sessionData?.email !== nextSessionData.email
        ) {
            sessionData = nextSessionData
            emitter.emit('sessionUpdated', sessionData)
        }
    }

    return {get, set, emitter}
}

export const sessionStore = getSessionStore()

// Listener for changes in sessionStore to keep SessionProvider up-to-date with latest values
export const useSessionUpdatedEventListener = (
    onSessionUpdated: (data: SessionData | undefined) => void,
) => {
    React.useEffect(() => {
        sessionStore.emitter.on('sessionUpdated', onSessionUpdated)
        return () => sessionStore.emitter.off('sessionUpdated', onSessionUpdated)
    }, [])
}

export function findPrimaryAccountToken(primaryAccountEmail: string, accessTokens: string[]) {
    return accessTokens.find((token) => {
        const tokenData = getTokenPayload(token)
        return tokenData?.data.user.email === primaryAccountEmail
    })
}

// Starts or refreshes the session in Kerberos and fetches and returns the access token.
// Session data set to sessionStorage to stay accessible for all kinds of request/data fetching
// methods outside React.Context
async function fetchSession(options?: {idToken?: string; companyId?: string; force?: boolean}) {
    // If we already have a valid JWT token stored let's use that.
    const sessionData = sessionStore.get()
    const prevAccessToken = sessionData?.accessToken ?? null
    const prevAccessTokens = sessionData?.accessTokens ?? null
    const previousEmail = sessionData?.email
    const tokenPayload = prevAccessToken ? getTokenPayload(prevAccessToken) : null
    if (prevAccessToken && tokenPayload && !options?.force) {
        if (getJwtExpiryMin(tokenPayload) > 10) {
            return {
                email: previousEmail,
                accessToken: prevAccessToken,
                accessTokens: prevAccessTokens?.length ? prevAccessTokens : null,
            }
        }
    }

    const authCheck = uuid()
    cookie.set('authCheck', authCheck, config.auth.authCheckExpire)

    // We are deliberately using axios directly here instead of the usual request helper
    // We don't need all the additional niceties that request provides (like injecting the token)
    // and thus avoid a circular dependency as well

    // Starts or refreshes the session in Kerberos and fetches and returns the access token
    // We also support two methods of handling session refreshes, the legacy and the OTP session with trusted devices
    // A session on a trusted device can be refreshed using a refresh token and passcode.
    const companyId = options?.companyId || getClientSessionId()
    const params = companyId ? {companyId} : {}
    const [response, legacyResponse] = await Promise.all([
        axios.request<SessionData>({
            url: SESSION_ROUTE,
            method: 'get',
            withCredentials: true,
            params: {companyId, idToken: options?.idToken},
        }),
        axios.request<SessionData>({
            url: LEGACY_SESSION_ROUTE,
            method: 'post',
            headers: {'X-Auth-Check': authCheck},
            withCredentials: true,
            params: options?.force ? {force: true, ...params} : {},
        }),
    ])

    // We wait until both requests are resolved and then we have a preference for the new session route.
    // We only decide to use legacy response if new session route does not return any useful info
    // while legacy route returns acces token
    if (
        legacyResponse.data?.accessToken &&
        !response.data?.accessToken &&
        !response.data?.email &&
        !response.data?.trustedEmails?.length
    ) {
        sessionStorage.setItem(SESSION_TYPE_KEY, SessionType.Legacy)
        sessionStore.set({accessToken: legacyResponse.data.accessToken})
        return legacyResponse.data
    }

    sessionStorage.setItem(SESSION_TYPE_KEY, SessionType.Otp)

    const {
        accessToken = null,
        accessTokens = null,
        email = null,
        trustedEmails = null,
    } = response.data

    let res = {}

    if (accessToken) {
        res = {accessToken, accessTokens}
    }

    // The access token returned for a user with mutiple active sessions is the one that was added last
    // Here we check to see if the return access token matches one that was selected by the user when switching accounts.
    // If it does, we use the access token matching that account instead of the one returned by the server.
    if (accessTokens) {
        const primaryAccountEmail = getPrimaryAccountEmail()

        if (primaryAccountEmail) {
            const primaryAccountAccessToken = findPrimaryAccountToken(
                primaryAccountEmail,
                accessTokens,
            )

            if (primaryAccountAccessToken) {
                res = {
                    accessToken: primaryAccountAccessToken,
                    accessTokens,
                    email: primaryAccountEmail,
                }
            } else {
                res = {...res, accessTokens}
            }
        }
    }

    // If no access token present but there are trusted emails and preselected email
    // we return these so that the session can be refreshed with a passcode
    if (!accessToken && trustedEmails?.length && email) {
        res = {
            trustedEmails,
            email,
        }
    }

    if (trustedEmails) {
        res = {
            ...res,
            trustedEmails,
        }
    }

    // There is no access token and no info to refresh the session
    if (!accessToken && !email && !trustedEmails?.length) {
        res = {accessToken: null}
    }

    sessionStore.set(res)

    return res
}

// Ends the session in Kerberos
async function logoutSession(removeTrustedDevice = true) {
    const authCheck = uuid()
    cookie.set('authCheck', authCheck, config.auth.authCheckExpire)
    const sessionType = sessionStorage.getItem(SESSION_TYPE_KEY)
    setPrimaryAccountEmail(null)

    // Using axios deliberately (see fetchSession for explanation)

    // During the transition period for 2FA, we will support all legacy routes
    // So based on the session type, we can log the user out appropriately.
    // We also invalidate the trusted device session when the user logs out.
    await axios.request<void>({
        url: sessionType === SessionType.Otp ? SESSION_LOGOUT_ROUTE : LEGACY_SESSION_LOGOUT_ROUTE,
        method: 'post',
        headers: sessionType === SessionType.Otp ? {} : {'X-Auth-Check': authCheck},
        withCredentials: true,
        // Here we remove the trusted session if the option is passed on logout and if the session is not the legacy type
        params: sessionType === SessionType.Otp ? {removeTrustedDevice} : undefined,
    })
}

type LoginSession = {
    email: string
    passcode: string | null
    otp: string
    trust: boolean
    loginToken: string | null
    otpType: OtpType
}

/**
 * Method to request access token to log user in for a fresh session
 * @param email User's email
 * @param passcode  User's passcode
 * @param otp One-Time-Password (OTP) sent to user's verified phone
 * @param trust Indicates whether the current browser is a trusted session
 * @param loginToken A token derived from a redirect from Single-Sign-On (SSO). This usually indicates an external provider login
 * @returns an accessToken or null
 */

export async function loginSession({
    email,
    passcode,
    otp,
    trust,
    loginToken,
    otpType,
}: LoginSession) {
    const result = await axios.request<SessionData | void>({
        url: SESSION_LOGIN_ROUTE,
        method: 'post',
        withCredentials: true,
        data: {
            email,
            otp,
            trust,
            ...(passcode && {passcode}),
            ...(loginToken && {loginToken}),
            ...(otpType === OtpType.GOOGLE_AUTH && {otpType}),
        },
    })
    return result.data ? result.data : {accessToken: null}
}

type VerifyLoginAuthData = {
    accessToken: string
    companyId: string | undefined
    partnerId: string | undefined
    accessTokens: string[]
    activeAccount: {
        accessToken: string
        email: string
    }
}

type VerifiedLoginResponse =
    | {
          result: 'authenticated'
          authenticationData: VerifyLoginAuthData
      }
    | {
          result: 'email_challenge_required'
          authenticationData: null
      }

export async function verifiedLoginSession({
    email,
    passcode,
    otp,
    trust,
    loginToken,
    codeChallenge,
    otpType,
    codeChallengeMethod,
}: {
    email: string
    passcode?: string | null
    loginToken?: string | null
    otp: OtpType | string
    otpType?: OtpType | string
    trust: boolean
    codeChallenge: string
    codeChallengeMethod: 'plain' | 'S256'
}): Promise<VerifiedLoginResponse> {
    const result = await axios.request<VerifiedLoginResponse>({
        url: SESSION_VERIFIED_LOGIN_ROUTE,
        method: 'post',
        withCredentials: true,
        data: {
            email,
            otp,
            codeChallenge,
            codeChallengeMethod,
            trust,
            clientType: 'web',
            ...(passcode && {passcode}),
            ...(loginToken && {loginToken}),
            ...(otpType === OtpType.GOOGLE_AUTH && {otpType}),
        },
    })

    if (!result.data) {
        throw new Error('Failed to login session')
    }

    return result.data
}

/**
 * Method to request access token to log user in for a fresh session
 * @param email logged-in email
 */
export async function setTrusted(email: string) {
    await axios.request<void>({
        url: SESSION_SET_TRUSTED_ROUTE,
        method: 'post',
        withCredentials: true,
        data: {
            email,
        },
    })
}

/**
 * Method to request access token to log user in.
 * This is for a return log in. What this means is that a user has trusted the device. On a trusted device,
 * a trusted session is kept active so that they only thing the user requires to log-in is their passcode
 * @param param.passcode User's passcode
 * @param param.email Email to authenticate account towards
 * @returns an accessToken or null
 */
async function loginSessionWithPasscode(payload: {passcode: string; email: string}) {
    const authCheck = uuid()
    cookie.set('authCheck', authCheck, config.auth.authCheckExpire)

    const result = await axios.request<SessionData>({
        url: SESSION_REFRESH_ROUTE,
        method: 'post',
        withCredentials: true,
        data: payload,
    })
    return result.data ? result.data : {accessToken: null}
}

/**
 * Method to request login with token
 * @param code Token to authenticate with (sent to email)
 * @param codeVerifier Client side generated code (stored in local storage)
 * @returns {accessToken: string; accessTokens: string[]} | null
 */
export async function verifyLoginAttempt(code: string, codeVerifier: string): Promise<SessionData> {
    const result = await axios.request<VerifyLoginAuthData>({
        url: VERIFY_LOGIN_ROUTE,
        method: 'post',
        withCredentials: true,
        data: {code, codeVerifier},
    })
    return result.data ? {...result.data, email: result.data?.activeAccount?.email} : {}
}

// Adjusts the behavior when for some reason we can't fetch the access token
// Retry and if still failing, redirect to the error page
const onErrorRetry: SWRConfiguration['onErrorRetry'] = (
    _error,
    _key,
    _option,
    revalidate,
    revalidateOptions,
) => {
    const retryCount = revalidateOptions.retryCount ?? 1
    if (retryCount >= 5) {
        throw new Error('Cannot fetch token')
    }

    setTimeout(async () => revalidate({retryCount: retryCount + 1}), retryCount * 2000)
}

// Cleanup all session-related storage and saved data, called when the session status
// changes from logged in to logged out
async function cleanupSession(options?: LogoutOptions) {
    sessionStore.set({accessToken: null, accessTokens: null, email: null})
    await logoutFreshchat()
    options?.onLogout?.()
    sessionStorage.clear()
    clearScopedTokens()
    clearClientSession()
    // we want full page reload, so it's not react-router
    window.location.assign(options?.afterLogoutPath ?? `/login`)
}

/**
 * Open a new session either through login or reauthenticating an existing session
 * It sets the session type to Otp
 * It sets a primary email account to help with the switch account flow
 * It starts a new bookkeeper session if a companyId is present
 * It force fetches the session to get the new access token
 * @param param.companyId // the primary companyId associated with the user
 * @param param.accessToken // the accesss token for the current session
 * @param param.accessTokens // the access tokens for all accounts associated with a device (or browser)
 * @param param.email // the email associated with the current session
 */
export async function openSession({companyId, accessToken, accessTokens, email}: SessionData) {
    //we should open bookkeeper session before setting anything to the sessionStore
    //because sessionStore.set will cause /user API re-fetch (see UserProvider)
    //and bookkeeper companyID should be already available in the local storage at that moment.
    //this is OK since we are not immediately redirecting (skipRedirect: true)
    if (companyId) {
        openClientSession(companyId)
    } else {
        clearClientSession()
    }

    sessionStorage.setItem(SESSION_TYPE_KEY, SessionType.Otp)
    sessionStore.set({accessToken, accessTokens, email})

    setPrimaryAccountEmail(email)

    // Clear localstorage params needed for registration
    removeParamsFromRegistration()

    // Here we force fetch the session, bypassing the check for jwt expiry
    // Without this, the old access tokens which are still valid will be used
    await fetchSession({force: true})
}

function getMutations(setIsLoggingOut: () => void) {
    /**
     * Ends the session of the currently logged in user and clears all the caches
     * and session-related local data. It also flushes the session token info in
     * cache to trigger the logout behavior
     * @param options.afterLogoutPath The page to redirect the user to after the page reload (defaults to `/login`)
     * @returns A promise that resolves when the user is logged out
     */
    async function logout(options?: LogoutOptions) {
        await logoutSession()
        setIsLoggingOut()
        cleanupSession(options)
    }

    /**
     * Method to log user in and set the session type
     * @param email
     * @param passcode
     * @param otp
     * @param trust
     * @param loginToken
     */

    async function login({
        email,
        passcode,
        otp,
        trust,
        loginToken,
        otpType,
    }: {
        email: string
        passcode: string | null
        otp: string
        trust: boolean
        loginToken: string | null
        otpType: OtpType
    }) {
        const {accessToken, accessTokens, companyId, partnerId} = await loginSession({
            email,
            passcode,
            otp,
            trust,
            loginToken,
            otpType,
        })

        await openSession({companyId, accessToken, accessTokens, email, partnerId})
    }

    async function verifiedLogin({
        email,
        passcode,
        otp,
        trust,
        loginToken,
        otpType,
        codeChallenge,
        codeChallengeMethod,
    }: {
        email: string
        passcode: string | null
        otp: string
        trust: boolean
        loginToken: string | null
        otpType: OtpType
        codeChallenge: string
        codeChallengeMethod: 'plain' | 'S256'
    }): Promise<VerifiedLoginResponse> {
        const response = await verifiedLoginSession({
            email,
            passcode,
            otp,
            trust,
            loginToken,
            otpType,
            codeChallenge,
            codeChallengeMethod,
        })

        if (response.result === 'authenticated') {
            await openSession({email, ...response.authenticationData})
        }

        return response
    }

    /**
     * Method to log user on a trusted device
     * This is used for a second time login.
     * @param param.passcode
     * @param param.email
     */
    async function loginWithPasscode({passcode, email}: {passcode: string; email: string}) {
        const {accessToken, accessTokens, companyId} = await loginSessionWithPasscode({
            passcode,
            email,
        })

        await openSession({companyId, accessToken, accessTokens, email})
    }

    /**
     * Method to log user on a trusted device
     * This is used for a second time login.
     * @param param.token
     */
    async function loginWithToken(token: string, codeVerifier: string) {
        const response = await verifyLoginAttempt(token, codeVerifier)

        await openSession(response)

        return response
    }

    /**
     * Method to log in the user during register flows
     * @param getToken
     */

    async function register(
        getToken: () => Promise<undefined | {accessToken: string; accessTokens?: string[]}>,
    ) {
        const response = await getToken()
        if (response) {
            // Cache the access token returned from Kerberos, this will be used
            // for any outgoing requests with the `auth: 'user'` option.
            const {accessToken, accessTokens} = response
            sessionStore.set({accessToken, accessTokens})

            // Cache session type
            sessionStorage.setItem(SESSION_TYPE_KEY, SessionType.Otp)

            // Clear localstorage params needed for registration
            removeParamsFromRegistration()

            // Force a refresh of the session, which will fetch a new access token
            // containing the newly created employee.
            await forceSessionRefresh()
        }
    }

    interface RegisterAdminPayload {
        firstName: string
        lastName: string
    }

    async function registerAdmin(
        createKerberosUser: () => Promise<
            undefined | {accessToken: string; accessTokens?: string[]}
        >,
        {firstName, lastName}: RegisterAdminPayload,
    ) {
        const response = await createKerberosUser()

        if (response) {
            // Cache the access token returned from Kerberos, this will be used
            // for any outgoing requests with the `auth: 'user'` option.
            const {accessToken} = response

            try {
                await request(`${config.endpoints.api}/rest/v1/employees`, {
                    auth: RequestAuth.USER,
                    headers: {
                        'X-Auth-Cache': 'ignore',
                        // eslint-disable-next-line string-to-lingui/missing-lingui-transformation
                        Authorization: `Bearer ${accessToken}`,
                    },
                    body: {firstName, lastName},
                    method: 'POST',
                })
            } catch (error) {
                reportError(error, 'Failed to create employee in sign up flow')
            }

            // Clear localstorage params needed for registration
            removeParamsFromRegistration()

            sessionStorage.setItem(SESSION_TYPE_KEY, SessionType.Otp)

            // Force a refresh of the session, which will fetch a new access token
            // containing the newly created employee.
            await forceSessionRefresh()
        }
    }

    return {
        logout,
        login,
        register,
        registerAdmin,
        loginWithPasscode,
        loginWithToken,
        verifiedLogin,
    }
}

// #endregion Private Helper Methods
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Public methods

/**
 * Custom React hook that allows to execute passed callback
 * only when entity (or account) is switched.
 * This might be useful to handle multi-entity side effects.
 * @param callback a function that will be executed only on entity/account switch.
 */
export function useEntitySwitchEffect(callback: () => void) {
    const tokenData = useTokenData()
    const key = `${tokenData?.user?.id}-${tokenData?.user?.cmp}`

    React.useEffect(() => {
        callback()
    }, [key])
}

/**
 * Custom React hook retrieving the access token for the currently
 * logged in user from the context
 * @returns The basic access token, if available
 */
export function useAccessToken(): string | null {
    const {accessToken} = React.useContext(SessionContext)
    return accessToken
}

/**
 * Custom React hook retrieving current email and list of trusted emails for a user on a trusted device
 * @returns email and list of trusted emails if available
 */
export function useSessionRefresh(): {
    email: string
    trustedEmails?: string[] | null
} | null {
    const {email, trustedEmails} = React.useContext(SessionContext)
    return email && trustedEmails?.length ? {email, trustedEmails} : null
}

/**
 * Custom React hook to get/set trusted status of the user's session
 * @param email - user's email to get/set trusted session status
 * @returns
 *  result.isTrusted - whether session is trusted for a given email,
 *  result.setTrusted - makes account session to be trusted for a given email
 */
export function useTrustedSession(email?: string) {
    const {trustedEmails} = React.useContext(SessionContext)

    return {
        isTrusted: email && trustedEmails?.includes(email),
        setTrusted: async () => {
            if (email) {
                await setTrusted(email)
                await forceSessionRefresh()
            }
        },
    }
}

/**
 * Custom React hook to get list of trusted emails
 * @returns an array of trusted emails
 */
export function useTrustedEmails() {
    const {trustedEmails} = React.useContext(SessionContext)

    return trustedEmails ?? []
}

/**
 * Custom hook to check if we can exchange a trusted session cookie for a new accessToken
 * @returns true/false to see if the user can refresh the session
 */
export function useShouldRefreshSession(): boolean {
    const sessionType = sessionStorage.getItem(SESSION_TYPE_KEY)
    const sessionStatus = useSessionStatus()
    const sessionRefresh = useSessionRefresh()

    return Boolean(
        sessionStatus === SessionStatus.Unknown &&
            sessionType === SessionType.Otp &&
            sessionRefresh,
    )
}

/**
 * Custom React hook retrieving the status of the session from the context
 * @returns The status of the session (unknown/logged in/logged out)
 */
export function useSessionStatus(): SessionStatus {
    const {status} = React.useContext(SessionContext)
    return status
}

/**
 * Custom React hook for checking if the user is currently logged in
 * @returns Boolean indicating whether the user is logged in
 */
export function useIsLoggedIn() {
    const {status} = React.useContext(SessionContext)
    return status === SessionStatus.LoggedIn
}

/**
 * Custom React hook for checking if the user is currently logged out
 * @returns Boolean indicating whether the user is logged out
 */
export function useIsLoggedOut() {
    const {status} = React.useContext(SessionContext)
    return status === SessionStatus.LoggedOut
}

/**
 * Custom hook that returns the logout method
 * @returns logout method
 */
export function useLogout() {
    const {logout} = React.useContext(SessionContext)
    return logout
}

/**
 * Custom hook to login. It returns the login method
 * This requires a passcode and a One-Time-Password (OTP)
 * @returns login method
 */
export function useLogin() {
    const {login} = React.useContext(SessionContext)
    return login
}

/**
 * Custom hook to login. It returns the login method
 * This requires a passcode, a One-Time-Password (OTP)
 * @returns login method
 */
export function useVerifiedLogin() {
    const {verifiedLogin} = React.useContext(SessionContext)
    return verifiedLogin
}

/**
 * Custom hook to login during register flow. It returns the register method
 * This requires a getToken function which return a promise with accessToken or undefined
 * @returns login method
 */
export function useRegister() {
    const {register, registerAdmin} = React.useContext(SessionContext)
    return {register, registerAdmin}
}

/**
 * Custom hook to login with passcode when previously logged in on a trusted device.
 * When a user is logging in, we ask if they want to trust the device. When their session expires after a few hours, they can request login again with their passcode.
 * **Trusting the device means that a trusted session is kept alive even when the session is expired.**
 *
 * @returns loginWithPasscode method
 */
export function usePasscodeLogin() {
    const {loginWithPasscode} = React.useContext(SessionContext)
    const userMutations = useUserMutations()
    const passcodeLogin: ReturnType<typeof getMutations>['loginWithPasscode'] = async (args) => {
        await loginWithPasscode(args)
        await userMutations.forceRefetchUser({
            companyId: getClientSessionId() ?? undefined,
        })
    }

    return passcodeLogin
}

/**
 * Custom hook to login with token
 * @returns loginWithPasscode method
 */
export function useLoginWithToken() {
    const {loginWithToken} = React.useContext(SessionContext)
    return loginWithToken
}

export interface LoggedInAccount {
    accessToken: string
    email: string
    connectedAccountEmails?: {email: string; trusted?: boolean}[]
}

export type AddAccountPayload = Partial<
    Pick<SessionData, 'email' | 'accessToken' | 'accessTokens' | 'companyId'>
> & {
    skipRedirect?: boolean
    location?: string
}

export type AddAccount = (payload: AddAccountPayload) => Promise<void>

export type SwitchAccountPayload = {
    email: string
    companyId?: string | null
    location?: string
    skipRedirect?: boolean
}

export type SwitchAccount = (payload: SwitchAccountPayload) => Promise<void>

/**
 * Custom hook to get the current logged in accounts (decoded access token)
 * @returns the current logged in accounts and a method to switch to another account
 */
export function useLoggedInAccounts(): {
    accounts: LoggedInAccount[]
    switchAccount: SwitchAccount
    addAccount: AddAccount
} {
    const userMutation = useUserMutations()
    const navigate = useNavigate()
    const {accountAccessTokens, accessToken: currentAccessToken} = React.useContext(SessionContext)
    const accounts: LoggedInAccount[] = ([...new Set(accountAccessTokens)] ?? [currentAccessToken])
        .filter(Boolean)
        .map((accessToken: string) => {
            const decodedData = getTokenPayload(accessToken)?.data
            const email = decodedData?.user.email
            const connectedAccountEmails = decodedData?.meta?.subSessionEmails

            if (!email) {
                return null
            }

            return {
                email,
                accessToken,
                connectedAccountEmails,
            }
        })
        .filter(notEmpty)

    const updateSession: AddAccount = async ({
        email,
        companyId,
        location = '/expenses',
        skipRedirect,
    }) => {
        // If a companyId, we start a new bookkeeper session
        // If we have a bookkeeper session and no companyId pass we close the bookkeeper session
        if (companyId) {
            openClientSession(companyId)
        } else {
            clearClientSession()
        }

        setPrimaryAccountEmail(email)

        // We store all available accessTokens in the SessionStore and ideally when we want to switch to another account
        // we should just set proper account data to the session store
        // like sessionStore.set({email, accessToken, accessTokens}).
        // But /user endpoint requires full session refresh to fetch new data.
        await fetchSession({force: true})

        clearScopedTokens()

        const options = companyId ? {companyId} : {}

        await userMutation.forceRefetchUser(options)

        if (!skipRedirect) {
            navigate(location)
        }
    }

    const switchAccount: SwitchAccount = async ({email, companyId, location, skipRedirect}) => {
        const associateAccount = accounts.find((account) => account.email === email)

        const token = associateAccount?.accessToken
        if (!isJwt(token ?? '') || !token) {
            return Promise.reject()
        }
        return updateSession({
            accessToken: token,
            accessTokens: accountAccessTokens,
            email,
            companyId,
            location,
            skipRedirect,
        })
    }

    return {
        accounts,
        switchAccount,
        addAccount: updateSession,
    }
}

/**
 * **DISCOURAGED** Use `useAccessToken` instead!
 *
 * Retrieves the access token for the currently logged in user, bypassing the React context
 * Use the `useAccessToken` instead, unless you have a good reason to rely on this behaviour.
 * The downside of this method is that the consumer of this method will not be notified when
 * the value of the access token changes for whatever reason
 * @returns The jwt access token, if available
 */
export function __getAccessToken(): string | null {
    const data = sessionStore.get()
    return data?.accessToken ?? null
}

/**
 * **DISCOURAGED** Use `useLogout` instead!
 *
 * Closes the session in Kerberos and clears session cache that in terms logs the user out.
 * Use the useLogout hooks instead when using from within React
 * @returns A promise that resolves when the user is logged out
 */
export async function __logout() {
    await logoutSession(true)
    sessionStorage.removeItem(SESSION_TYPE_KEY)
    sessionStore.set({accessToken: null, accessTokens: null, email: null})
}

/**
 * Allows to force refresh the session - it will invalidate the currently used
 * token across all of the Pleo systems, and fetch a new one
 * @returns A promise that resolves when the session has been refreshed
 */
export async function forceSessionRefresh(idToken?: string) {
    await fetchSession({force: true, idToken})
}

/**
 * Allows to force refresh the session with a companyId to allow Partner Member's
 * to perform actions on thier clients from within the Partner Portal, i.e. without
 * necessitating app refresh with openClientSession. It will clear the
 * current bookkeeper session and invalidate the currently used token across all
 * of the Pleo systems, and fetch a new one
 * @param companyId
 * @returns A promise that resolves when the session has been refreshed
 */
export async function getPartnerClientSession(companyId: string) {
    clearClientSession()
    return await fetchSession({companyId, force: true})
}

// #endregion Public Methods
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Scoped Tokens
// Manages fetching, storing, retrieving and invalidating scoped tokens
// Scoped tokens are required for certain actions instead of the regular access token
// and require a pin number to retrieve. They are not auto-refreshed,
// and are cached for SCOPED_TOKEN_LIFE_MS. Scoped tokens wouldn't benefit from being
// fetched with SWR so instead we're managing our own cache here

const SCOPED_TOKEN_LIFE_MS = 1000 * 60 * 10 // 10 min
const SCOPED_TOKEN_LIFE_MAX_MS = 1000 * 60 * 60 * 24 // 24 hours

const scopedTokens = new Map<RequestScope, string>()
const scopedTokenTimeouts = new Map<RequestScope, number>()

// Sets the token in cache and set a timeout to clear it after SCOPED_TOKEN_LIFE_MS
// If the token is already cached, resets the timeout
function cacheScopedToken(scope: RequestScope, token: string) {
    // We are not caching the card details scoped token, so that the user
    // needs to enter the pin every time to show card details
    if (scope === RequestScope.CARD_DETAILS) {
        return
    }

    if (scopedTokenTimeouts.has(scope)) {
        window.clearTimeout(scopedTokenTimeouts.get(scope))
    }

    // In case of JWT we should read expiration time right from the token
    const tokenPayload = getTokenPayload(token)
    invariant(tokenPayload, 'Expected JWT token')
    const jwtExpiryMs = getJwtExpiryMs(tokenPayload)

    // We limit the scoped token life to SCOPED_TOKEN_LIFE_MAX_MS, so ensure we never overflow
    // the setTimeout max value (24.8 days).
    const scopedTokenLifeMs =
        jwtExpiryMs <= SCOPED_TOKEN_LIFE_MAX_MS ? jwtExpiryMs : SCOPED_TOKEN_LIFE_MS

    if (jwtExpiryMs > SCOPED_TOKEN_LIFE_MAX_MS) {
        // This is not the end of the world, but this should never happen.
        // We do see this happening in some Cypress test with mocked dates though.
        reportError(new Error('JWT expiry too long'), null, {
            jwtExpiryMs,
        })
    }

    scopedTokens.set(scope, token)
    const timeoutId = window.setTimeout(() => scopedTokens.delete(scope), scopedTokenLifeMs)
    scopedTokenTimeouts.set(scope, timeoutId)
}

// Fetches the token via a Pin Modal if the pin is not provided,
// or directly via API call if the pin is provided. Caches the returned token.
async function fetchScopedToken(
    scope: RequestScope,
    pin?: string,
    message?: string,
    description?: string,
    elevatedResourceIds?: string[],
) {
    let response: {
        accessToken: string
        pin?: string
    }
    if (!pin) {
        let publicKey: string | null = null
        if (scope === RequestScope.CARD_DETAILS) {
            const keypair = await getKeyPair()
            publicKey = keypair.publicKey
        }
        const result = await ModalPinLogin.showScoped(
            scope,
            publicKey,
            true,
            message,
            description,
            elevatedResourceIds,
        )
        if (result.status === 'cancelled') {
            return {}
        }
        response = result.value
    } else {
        const companyId = getClientSessionId()
        response = await apiAuthPin.login(pin, companyId, scope, null, elevatedResourceIds)
    }
    // Do not cache token when requested for specific resourceIds
    if (!elevatedResourceIds?.length) {
        cacheScopedToken(scope, response.accessToken)
    }

    return {
        accessToken: response.accessToken,
        pin: pin ?? response.pin,
    }
}

/**
 * Clears all cached scoped tokens regardless of their age
 */
export function clearScopedTokens() {
    scopedTokens.clear()
    scopedTokenTimeouts.forEach((timeoutId) => {
        window.clearTimeout(timeoutId)
    })
    scopedTokenTimeouts.clear()
}

/**
 * Returns multiple scope tokens in one go, only asks for the pin once
 * It will fetch the tokens even if they are already cached (i.e. force by default)
 * @param scopes The list of scopes for which to fetch tokens
 * @returns true if token was fetched and false if it was already present
 */
export async function ensureScopedTokens(
    scopes: RequestScope[],
    options?: {force?: boolean; message?: string; description?: string},
) {
    // short-circuit if all requested scoped token are available, unless a force refresh requested
    if (!options?.force && scopes.every((scope) => scopedTokens.has(scope))) {
        return false
    }

    // We are fetching the token for the first scope using the pin modal
    // and using the pin entered by the user to fetch the rest of scoped tokens
    const pinModalResponse = await fetchScopedToken(
        scopes[0],
        undefined,
        options?.message,
        options?.description,
    )
    // An empty object is returned by fetchScopedToken if the user
    // cancel instead of typing a code in Pin code the modal
    if (R.isEmpty(pinModalResponse)) {
        throw Error(PIN_CANCELLED_BY_USER_ERROR_STATUS)
    }
    for (let i = 1; i < scopes.length; i++) {
        await fetchScopedToken(scopes[i], pinModalResponse.pin, options?.message)
    }
    return true
}

/**
 * Fetches a single scoped token. If the token is already cached, it will return the cached value
 * unless a `force` option is provided. Only caches non-card scopes.
 * @param scope The scopes for which to fetch token
 * @param options.force If set, the user will be asked for pin and the token will be refreshed
 * @returns A record mapping scopes to scoped access token
 */
export async function getScopedToken(
    scope: RequestScope,
    options?: {force?: boolean; elevatedResourceIds?: string[]},
) {
    // use the cached scoped token is available, unless a force refresh requested
    if (!options?.force && scopedTokens.has(scope) && !options?.elevatedResourceIds?.length) {
        return scopedTokens.get(scope)
    }

    const {accessToken} = await fetchScopedToken(
        scope,
        undefined,
        undefined,
        undefined,
        options?.elevatedResourceIds,
    )
    return accessToken
}

// #endregion Scoped Tokens
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Cryptographic KeyPair

type ResolvedValue<T> = T extends PromiseLike<infer U> ? U : T
export type KeyPair = ResolvedValue<ReturnType<typeof generateKeyPair>>
let keyPair: KeyPair

export async function getKeyPair(options?: {force: true}) {
    if (!options?.force && keyPair) {
        return keyPair
    }

    keyPair = await generateKeyPair()
    return keyPair
}

// #endregion Cryptographic Keypair
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Client session
// @pleo/companyId saved in the local storage determines which company the user
// is currently "working for". This applies only to bookkeepers (independent or via a partner)

export const CLIENT_SESSION_STORAGE_KEY = '@pleo/companyId'

export function openClientSession(companyId: string) {
    localStorage.setItem(CLIENT_SESSION_STORAGE_KEY, companyId)
}

function clearClientSession() {
    localStorage.removeItem(CLIENT_SESSION_STORAGE_KEY)
}

export function getClientSessionId() {
    return localStorage.getItem(CLIENT_SESSION_STORAGE_KEY)
}

// #endregion Client session
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region primary account email
// Allows managing active account email for account-switch feature

export function getPrimaryAccountEmail() {
    return localStorage.getItem(PRIMARY_ACCOUNT_EMAIL)
}

function setPrimaryAccountEmail(email: string | null | undefined) {
    if (email) {
        localStorage.setItem(PRIMARY_ACCOUNT_EMAIL, email)
    } else {
        localStorage.removeItem(PRIMARY_ACCOUNT_EMAIL)
    }
}

// #endregion primary account email
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region Force login
// Allows to send user to the login page and specify what location they should end up
// on after a successful login.

export const LOGIN_REDIRECT_PATH_STORAGE_KEY = 'gotoAfterLogin'
type ForceLoginLocationState = {email?: string; redirect?: string}

/**
 * Handles the redirect logic after successful login. To be used in the global layout component.
 */
export function useHandleLoginRedirect() {
    const navigate = useNavigate()
    const location = useLocation()
    const sessionStatus = useSessionStatus()

    React.useEffect(() => {
        if (sessionStatus !== SessionStatus.LoggedIn) {
            return
        }
        const redirectPath = sessionStorage.getItem(LOGIN_REDIRECT_PATH_STORAGE_KEY)
        sessionStorage.removeItem(LOGIN_REDIRECT_PATH_STORAGE_KEY)
        if (redirectPath) {
            navigate(redirectPath, {replace: true})
        }
    }, [location.pathname, sessionStatus])
}

/**
 * Handles setting up the redirect on the login page.
 */
export function useHandleLoginState() {
    const location = useLocation()
    const state = (location.state ?? {}) as ForceLoginLocationState

    React.useEffect(() => {
        const redirectAfterLogin = state.redirect
        if (redirectAfterLogin && redirectAfterLogin !== '/') {
            sessionStorage.setItem(LOGIN_REDIRECT_PATH_STORAGE_KEY, redirectAfterLogin)
        }
    }, [])
}

export function useForceLogin() {
    const navigate = useNavigate()
    const location = useLocation()
    /**
     * Navigates the anonymous user the login page, ensuring that the user will be redirected back
     * to the current page after the successful login.
     * @param email The email to pre-fill the login form with
     */
    return function forceLogin(email?: string) {
        const state: ForceLoginLocationState = {
            email,
            redirect: location.pathname + location.search,
        }
        navigate('/login', {state})
    }
}

// #endregion Force login
////////////////////////////////////////////////////////////////////////////////

/**
 * Handles forcing session log out propagation across browser tabs
 * Since introducing jwts, we don't request a new token until the previous one expires. The expiration time for a token today is 10 minutes.
 * So when a user logs out on an active browser tab, we need to force the log out of all other tabs.
 */
function useForceSessionCleanup() {
    const STORAGE_SESSION_STATUS_KEY = 'sessionStatus'

    function handleForeignSessionStatusChange(e: StorageEvent) {
        if (e.key !== STORAGE_SESSION_STATUS_KEY) {
            return
        }
        try {
            const status = localStorage.getItem(STORAGE_SESSION_STATUS_KEY)
            if (status === SessionStatus.LoggedOut) {
                __logout()
            }
        } catch (error) {
            reportError(error, 'Failed to get session status from local storage')
        }
    }

    function handleLocalSessionStatusChange(data: SessionData | undefined) {
        const status = deriveSessionStatusFromSessionData(data ?? {})
        try {
            localStorage.setItem(STORAGE_SESSION_STATUS_KEY, status)
        } catch (error) {
            reportError(error, 'Failed to set session status in local storage')
        }
    }

    useSessionUpdatedEventListener(handleLocalSessionStatusChange)

    React.useEffect(() => {
        window.addEventListener('storage', handleForeignSessionStatusChange)

        return () => window.removeEventListener('storage', handleForeignSessionStatusChange)
    }, [])
}

/**
 * A method to store and return a login challenge
 * This is used for login challenges for the email verification flow
 * @returns {codeChallenge, codeChallengeMethod}
 */
export function useLoginAttemptChallenge() {
    const codeChallengeMethod = 'plain' as const

    React.useEffect(() => {
        if (localStorage.getItem('loginChallenge')) {
            return
        }
        localStorage.setItem('loginChallenge', `pleo-login-code-challenge-${uuid()}`)
    }, [])

    return {codeChallenge: localStorage.getItem('loginChallenge'), codeChallengeMethod}
}

/**
 * A method to derive the session status from the session data.
 * Translate the vague null/undefined/string type of the access token
 * to an explicitly defined session status. If the user is in the process
 * of logging out or if they are can refresh their session on a trusted device, we treat this as an unknown status
 */
function deriveSessionStatusFromSessionData({accessToken}: SessionData, isLoggingOut?: boolean) {
    if (accessToken) {
        return SessionStatus.LoggedIn
    } else if (accessToken === null && !isLoggingOut) {
        return SessionStatus.LoggedOut
    }

    return SessionStatus.Unknown
}

/**
 * Compare two arrays to see if they are the same
 * For the current use case, it only compares array of primitive values
 * @param a an array of string or number
 * @param b an array of string or number
 * @returns true or false if a and b are equal
 */
export const isSame = (a: Array<string | number>, b: Array<string | number>) => {
    return (
        a.every((item: string | number) => b.includes(item)) &&
        b.every((item: string | number) => a.includes(item))
    )
}

let refreshPromise: Promise<void> | null = null
/**
 * Force refresh the session, but only allow a single refresh at any given moment
 */
export async function memoForceSessionRefresh() {
    if (!refreshPromise) {
        refreshPromise = forceSessionRefresh()
    }
    await refreshPromise
    refreshPromise = null
}
